import React, { useMemo, useEffect } from 'react';
import { Form, Input } from 'antd';
import { Button } from '@/components/ui/Button';
import AppModal from '@/pages/inside/tenant/AppModal';
import { CreateRoleModalBody, CreateRoleModalFooter, ModalBlockLabel } from '@/pages/inside/tenant/style';
import { SendOutlined } from '@ant-design/icons';
import { LINK_URL_EXAMPLE_HELP, normalizeUrlOnFirstType, validateLinkUrlInput } from '@utils/linkUrlValidation.jsx';
import styled from 'styled-components';

const LinkModalFormWrap = styled.div`
    .ant-form-item-explain,
    .ant-form-item-explain-error {
        font-size: 12px;
        line-height: 1.25;
        margin-top: 2px;
    }
`;

export default function AddUrlModal({
    open,
    onCancel,
    form: externalForm,
    onSubmit,
    title = 'Add link',
    uploadAttachmentsPending = false,
}) {
    const [fallbackForm] = Form.useForm();
    const activeForm = externalForm || fallbackForm;

    useEffect(() => {
        if (open) {
            activeForm.resetFields();
        }
    }, [open, activeForm]);

    const urlValue = Form.useWatch('url', activeForm);

    const urlStartsOk = useMemo(() => {
        const v = typeof urlValue === 'string' ? urlValue.trim() : '';
        if (!v) return false;
        return /^https?:\/\//i.test(v) || validateLinkUrlInput(v).ok;
    }, [urlValue]);

    const handleFinalSubmit = () => {
        activeForm
            .validateFields()
            .then((values) => {
                const rawUrl = values.url ? values.url.trim() : '';
                const linkTitle = values.title ? values.title.trim() : '';
                const url = /^https?:\/\//i.test(rawUrl) ? rawUrl : `https://${rawUrl}`;
                onSubmit?.(url, linkTitle || url);
                activeForm.resetFields();
            })
            .catch((err) => {
                console.error('AddUrlModal validation failed:', err);
            });
    };

    return (
        <AppModal
            title={title}
            open={open}
            onCancel={onCancel}
            footer={
                <CreateRoleModalFooter $inFooterSlot style={{ padding: '10px 20px' }}>
                    <Button
                        $primary={false}
                        $size="14px"
                        onClick={onCancel}
                    >
                        Cancel
                    </Button>
                    <Button
                        $primary
                        $blackOnLight
                        $size="14px"
                        loading={uploadAttachmentsPending}
                        disabled={!urlStartsOk}
                        disabledTooltip={!urlStartsOk ? 'URL must start with "https://" (example: https://example.com)' : undefined}
                        icon={<SendOutlined />}
                        onClick={handleFinalSubmit}
                    >
                        {typeof title === 'string' && (title.includes('Attach') || title.includes('URL')) ? 'Attach' : 'Add link'}
                    </Button>
                </CreateRoleModalFooter>
            }
            size="sm"
        >
            <CreateRoleModalBody $flush $noMaxHeight>
                <LinkModalFormWrap style={{ padding: '12px 20px 16px 20px' }}>
                    <Form form={activeForm} layout="vertical" requiredMark={false}>
                        <Form.Item noStyle shouldUpdate={(prev, next) => prev?.url !== next?.url}>
                            {() => {
                                const urlErrors = activeForm?.getFieldError?.('url') || [];
                                const showExample = urlErrors.length === 0;
                                return (
                                    <Form.Item
                                        name="url"
                                        style={{ marginBottom: 14 }}
                                        label={<ModalBlockLabel>URL</ModalBlockLabel>}
                                        rules={[
                                            {
                                                validator: async (_rule, value) => {
                                                    const check = validateLinkUrlInput(value);
                                                    if (check.ok) return Promise.resolve();
                                                    return Promise.reject(new Error(check.message || 'Enter a valid URL'));
                                                },
                                            },
                                        ]}
                                        extra={showExample ? LINK_URL_EXAMPLE_HELP : null}
                                    >
                                        <Input
                                            placeholder="https://..."
                                            size="large"
                                        />
                                    </Form.Item>
                                );
                            }}
                        </Form.Item>
                        <Form.Item name="title" style={{ marginBottom: 0 }} label={<ModalBlockLabel>Link text</ModalBlockLabel>}>
                            <Input placeholder="Optional display text" size="large" />
                        </Form.Item>
                    </Form>
                </LinkModalFormWrap>
            </CreateRoleModalBody>
        </AppModal>
    );
}
